import AppConstants from "./appConstants";

export type CellTypeMenuOption = {
  name: string;
  label?: string;
  type?: "divider";
  class?: string;
  children?: CellTypeMenuOption[];
  clickable?: boolean;
};

export type CellTypeMenuTree = CellTypeMenuOption[];
const RBC_NORMAL_OPTIONS: CellTypeMenuOption[] = [
  { label: "Normal RBC", name: "NormalRBC" },
];

const RBC_SHAPE_OPTIONS: CellTypeMenuOption[] = [
  { label: "Elliptocytes", name: "Elliptocytes" },
  { label: "Ovalocytes", name: "Ovalocytes" },
  { label: "Teardrop cells", name: "TeardropCell" },
  { label: "Schistocytes", name: "Schistocyte" },
  { label: "Sickle cells", name: "SickleCell" },
  { label: "Spherocytes", name: "Spherocyte" },
  { label: "Bite cells", name: "BiteCell" },
  { label: "Blister cells", name: "BlisterCell" },
  { label: "Irregularly contracted cells", name: "IrregularlyContractedCell" },
  { label: "Acanthocytes", name: "Acanthocyte" },
  { label: "Echinocytes", name: "BurrCells" },
  { label: "Stomatocytes", name: "Stomatocyte" },
  { label: "Target cells", name: "TargetCell" },
];

const RBC_SIZE_COLOR_OPTIONS: CellTypeMenuOption[] = [
  { label: "Hypochromic cells", name: "HypochromicRedCell" },
  { label: "Macrocytes", name: "Macrocytes" },
  { label: "Oval macrocytes", name: "OvalMacrocytes" },
  { label: "Microcytes", name: "Microcytes" },
  { label: "Polychromasia", name: "PolychromaticCell" },
];

const RBC_INCLUSION_OPTIONS: CellTypeMenuOption[] = [
  { label: "Basophilic stippling", name: "BasophilicStippling" },
  { label: "Howell-Jolly bodies", name: "HowellJollyBody" },
  { label: "Pappenheimer bodies", name: "PappenheimerBody" },
];

const RBC_DISTRIBUTION_OPTIONS: CellTypeMenuOption[] = [
  { label: "Agglutination", name: "Agglutination" },
  { label: "Rouleaux", name: "Rouleaux" },
];

const RBC_UNCLASSIFIED_OPTIONS: CellTypeMenuOption[] = [
  { label: "Unclassified", name: "UnclassifiedRBC" },
];

const RBC_CELL_TYPES: string[] = [
  ...RBC_NORMAL_OPTIONS,
  ...RBC_SHAPE_OPTIONS,
  ...RBC_SIZE_COLOR_OPTIONS,
  ...RBC_INCLUSION_OPTIONS,
  ...RBC_DISTRIBUTION_OPTIONS,
  ...RBC_UNCLASSIFIED_OPTIONS,
].map((option) => option.name);

export const WBC_CELL_TYPE_TREE: CellTypeMenuTree = [
  {
    label: "Neutrophil",
    name: "Neutrophil",
    children: [{ label: "Dysplastic Neutrophil", name: "DysplasticNeutrophil" }],
  },
  {
    label: "Lymphocyte",
    name: "Lymphocyte",
    children: [
      { label: "Large Granular Lymphocyte", name: "LargeGranularLymphocyte" },
      { label: "Reactive Lymphocyte 1", name: "ReactiveLymphocyte1" },
      { label: "Reactive Lymphocyte 2", name: "ReactiveLymphocyte2" },
      { label: "Reactive Lymphocyte 3", name: "ReactiveLymphocyte3" },
      { label: "Small mature lymphocyte", name: "SmallMatureLymphocyte" },
      { label: "Hairy Cell", name: "HairyCell" },
      { label: "Mantle Cell", name: "MantleCell" },
      { label: "SMZL Cell", name: "SMZLCell" },
      { label: "Follicular Lymphoma Cell", name: "FollicularLymphomaCell" },
      { label: "Burkitt Cell", name: "BurkittCell" },
      { label: "Sézary Cell", name: "SezaryCell" },
      { label: "T Lymphoma Leukemia Cell", name: "TLymphomaLeukemiaCell" },
      { label: "Large Atypical Lymphoid Cell", name: "LargeAtypicalLymphoidCell" },
      { label: "Prolymphocyte", name: "Prolymphocyte" },
      { label: "Lymphoplasmacytic Cell", name: "LymphoplasmacyticCell" },
    ],
  },
  { label: "Monocyte", name: "Monocyte" },
  { label: "Promonocyte", name: "Promonocyte" },
  { label: "Eosinophil", name: "Eosinophil" },
  { label: "Basophil", name: "Basophil" },
  { label: "Metamyelocyte", name: "Metamyelocyte" },
  { label: "Myelocyte", name: "Myelocyte" },
  {
    label: "Promyelocyte",
    name: "Promyelocyte",
    children: [{ label: "PromyelocyteAPML", name: "PromyelocyteAPML" }],
  },
  {
    label: "Blast",
    name: "Blast",
    children: [
      { label: "Lymphoblast", name: "Lymphoblast" },
      { label: "Monoblast", name: "Monoblast" },
      { label: "Myeloblast", name: "Myeloblast" },
    ],
  },
  { label: "Plasma Cell", name: "PlasmaCell" },
  { label: "NRBC", name: "NRBC" },
  { label: "Dysplastic Cell (Non-neutrophil)", name: "DysplasticCellNonNeutrophil" },
  { label: "Smudge Cell", name: "SmudgeCell" },
  { label: "Burst Broken Cell", name: "BurstBrokenCell" },
  { label: "Artifact", name: "Artifact" },
  { label: "Giant Platelet", name: "GiantPlatelet" },
  { label: "Large Platelet", name: "LargePlatelet" },
  { label: "Platelet Clump", name: "PlateletClump" },
  { label: "Unclassified", name: "Unclassified" },
];

export const XAI_NTU_WBC_CELL_TYPE_TREE: CellTypeMenuTree = [
  {
    label: "Segmented Neutrophil",
    name: "SegmentedNeutrophil",
    children: [{ label: "Segmented Neutrophil Toxic Gran", name: "SegmentedNeutrophilToxicGran" }],
  },
  {
    label: "Band Neutrophil",
    name: "BandNeutrophil",
    children: [{ label: "Band Neutrophil Toxic Gran", name: "BandNeutrophilToxicGran" }],
  },
  {
    label: "Dysplastic Neutrophil",
    name: "DysplasticNeutrophil",
  },
  {
    label: "Lymphocyte",
    name: "Lymphocyte",
    children: [
      { label: "Reactive Lymphocyte Type 1", name: "ReactiveLymphocyte1" },
      { label: "Reactive Lymphocyte Type 2", name: "ReactiveLymphocyte2" },
      { label: "Reactive Lymphocyte Type 3", name: "ReactiveLymphocyte3" },
    ],
  },
  {
    label: "Monocyte",
    name: "Monocyte",
    children: [{ label: "Abnormal Monocyte", name: "AbnormalMonocyte" }],
  },
  {
    label: "Eosinophil",
    name: "Eosinophil",
    children: [{ label: "Dysplastic Eosinophil", name: "DysplasticEosinophil" }],
  },
  {
    label: "Basophil",
    name: "Basophil",
  },
  {
    label: "Metamyelocyte",
    name: "Metamyelocyte",
    children: [{ label: "Dysplastic Metamyelocyte", name: "DysplasticMetamyelocyte" }],
  },
  {
    label: "Myelocyte",
    name: "Myelocyte",
    children: [{ label: "Dysplastic Myelocyte", name: "DysplasticMyelocyte" }],
  },
  {
    label: "Promyelocyte",
    name: "Promyelocyte",
    children: [{ label: "Promyelocyte APML", name: "PromyelocyteAPML" }],
  },
  { label: "Blast", name: "Blast" },
  { label: "Plasma Cell", name: "PlasmaCell" },
  { label: "NRBC", name: "NRBC" },
  { label: "Smudge Cell", name: "SmudgeCell" },
  { label: "Burst Broken Cell", name: "BurstBrokenCell" },
  { label: "Artifact", name: "Artifact" },
  { label: "Large Platelet", name: "LargePlatelet" },
  { label: "Giant Platelet", name: "GiantPlatelet" },
  { label: "Platelet Clump", name: "PlateletClump" },
  { label: "Unclassified", name: "Unclassified" },
];

export const LPC_RBC_ASUS_WBC_CELL_TYPE_TREE: CellTypeMenuTree = [
  {
    label: "Neutrophil",
    name: "Neutrophil",
    children: [{ label: "Dysplastic Neutrophil", name: "DysplasticNeutrophil" }],
  },
  {
    label: "Lymphocyte",
    name: "Lymphocyte",
    children: [
      { label: "Reactive Lymphocyte", name: "ReactiveLymphocyte" },
      { label: "Large Granular Lymphocyte (LGL)", name: "LargeGranularLymphocyte" },
      { label: "Mantle Cell", name: "MantleCell" },
      { label: "SMZL Cell", name: "SMZLCell" },
      { label: "Hairy Cell", name: "HairyCell" },
      { label: "Adult T-Cell Leukemia/Lymphoma", name: "AdultTCellLeukemiaLymphomaCell" },
      { label: "Sézary Cell", name: "SezaryCell" },
      { label: "Burkitt Cell", name: "BurkittCell" },
      { label: "Follicular Lymphoma Cell", name: "FollicularLymphomaCell" },
      { label: "Large Atypical Lymphoid Cell", name: "LargeAtypicalLymphoidCell" },
      { label: "Prolymphocyte", name: "Prolymphocyte" },
      { label: "Lymphoplasmacytoid Cell", name: "LymphoplasmacyticCell" },
      { label: "Atypical Lymphoid Cell", name: "AtypicalLymphoidCell" },
    ],
  },
  {
    label: "Monocyte",
    name: "Monocyte",
  },
  {
    label: "Promonocyte",
    name: "Promonocyte",
  },
  {
    label: "Eosinophil",
    name: "Eosinophil",
  },
  {
    label: "Basophil",
    name: "Basophil",
  },
  {
    label: "Metamyelocyte",
    name: "Metamyelocyte",
  },
  {
    label: "Myelocyte",
    name: "Myelocyte",
  },
  {
    label: "Promyelocyte",
    name: "Promyelocyte",
  },
  {
    label: "Blast",
    name: "Blast",
    children: [
      { label: "Lymphoblast", name: "Lymphoblast" },
      { label: "Monoblast", name: "Monoblast" },
      { label: "Myeloblast", name: "Myeloblast" },
    ],
  },
  {
    label: "Plasma Cell",
    name: "PlasmaCell",
  },
  {
    label: "NRBC",
    name: "NRBC",
  },
  {
    label: "Smudge Cell",
    name: "SmudgeCell",
  },
  {
    label: "Burst Broken Cell",
    name: "BurstBrokenCell",
  },
  {
    label: "Artifact",
    name: "Artifact",
  },
  {
    label: "Large Platelet",
    name: "LargePlatelet",
  },
  {
    label: "Giant Platelet",
    name: "GiantPlatelet",
  },
  { label: "Unclassified", name: "Unclassified" },
];

export const BONE_MARROW_WBC_CELL_TYPE_TREE: CellTypeMenuTree = [
  {
    label: "Neutrophil",
    name: "Neutrophil",
    children: [{ label: "Dysplastic Neutrophil", name: "DysplasticNeutrophil" }],
  },
  {
    label: "Lymphocyte",
    name: "Lymphocyte",
    children: [
      { label: "Large Granular Lymphocyte", name: "LargeGranularLymphocyte" },
      { label: "Mantle Cell", name: "MantleCell" },
      { label: "SMZL Cell", name: "SMZLCell" },
      { label: "Hairy Cell", name: "HairyCell" },
      { label: "Adult T Cell Leukemia/Lymphoma Cell", name: "AdultTCellLeukemiaLymphomaCell" },
      { label: "Sézary Cell", name: "SezaryCell" },
      { label: "Burkitt Cell", name: "BurkittCell" },
      { label: "Follicular Lymphoma Cell", name: "FollicularLymphomaCell" },
      { label: "Large Atypical Lymphoid Cell", name: "LargeAtypicalLymphoidCell" },
      { label: "Prolymphocyte", name: "Prolymphocyte" },
      { label: "Lymphoplasmacytic Cell", name: "LymphoplasmacyticCell" },
      { label: "Atypical Lymphoid Cell", name: "AtypicalLymphoidCell" },
    ],
  },
  {
    label: "Monocyte",
    name: "Monocyte",
    children: [{ label: "Abnormal Monocyte", name: "AbnormalMonocyte" }],
  },
  { label: "Promonocyte", name: "Promonocyte" },
  {
    label: "Eosinophil",
    name: "Eosinophil",
    children: [{ label: "Dysplastic Eosinophil", name: "DysplasticEosinophil" }],
  },
  { label: "Basophil", name: "Basophil" },
  {
    label: "Metamyelocyte",
    name: "Metamyelocyte",
    children: [{ label: "Dysplastic Metamyelocyte", name: "DysplasticMetamyelocyte" }],
  },
  {
    label: "Myelocyte",
    name: "Myelocyte",
    children: [{ label: "Dysplastic Myelocyte", name: "DysplasticMyelocyte" }],
  },
  {
    label: "Promyelocyte",
    name: "Promyelocyte",
    children: [{ label: "PromyelocyteAPML", name: "PromyelocyteAPML" }],
  },
  {
    label: "Blast",
    name: "Blast",
    children: [
      { label: "Lymphoblast", name: "Lymphoblast" },
      { label: "Monoblast", name: "Monoblast" },
      { label: "Myeloblast", name: "Myeloblast" },
    ],
  },
  { label: "Plasma Cell", name: "PlasmaCell" },
  { label: "NRBC", name: "NRBC", children: [{ label: "Dysplastic NRBC", name: "DysplasticNRBC" }] },
  { label: "Histiocytes", name: "Histiocytes" },
  {
    label: "Megakaryocytes",
    name: "Megakaryocytes",
    children: [{ label: "Dysplastic Megakaryocyte", name: "DysplasticMegakaryocyte" }],
  },
  { label: "Mast Cell", name: "MastCell" },  
  { label: "Smudge Cell", name: "SmudgeCell" },
  { label: "Artifact", name: "Artifact" },
  { label: "Unclassified", name: "Unclassified" },
];

export const RBC_CELL_TYPE_TREE: CellTypeMenuTree = [
  ...RBC_NORMAL_OPTIONS,
  {
    label: "Shape",
    name: "RBCShape",
    class: "vue-simple-context-menu__category",
    clickable: false,
    children: RBC_SHAPE_OPTIONS,
  },
  {
    label: "Size & Colour",
    name: "RBCSizeColour",
    class: "vue-simple-context-menu__category",
    clickable: false,
    children: RBC_SIZE_COLOR_OPTIONS,
  },
  {
    label: "Inclusion",
    name: "RBCInclusion",
    class: "vue-simple-context-menu__category",
    clickable: false,
    children: RBC_INCLUSION_OPTIONS,
  },
  {
    label: "Distribution",
    name: "RBCDistribution",
    class: "vue-simple-context-menu__category",
    clickable: false,
    children: RBC_DISTRIBUTION_OPTIONS,
  },
  {
    label: "Unclassified",
    name: "RBCUnclassified",
    class: "vue-simple-context-menu__category",
    clickable: false,
    children: RBC_UNCLASSIFIED_OPTIONS,
  },
];

export function getDrawOptions({
  isRbc = false,
  projectKey = null,
  consts = null,
}: {
  isRbc?: boolean;
  projectKey?: string | null;
  consts?: typeof AppConstants | null;
} = {}): CellTypeMenuTree {
  if (isRbc) return RBC_CELL_TYPE_TREE;
  if (consts && projectKey === consts.PROJECT.XAI_NTU) {
    return XAI_NTU_WBC_CELL_TYPE_TREE;
  }
  if (consts && projectKey === consts.PROJECT.LPC_RBC_ASUS) {
    return LPC_RBC_ASUS_WBC_CELL_TYPE_TREE;
  }
  if (consts && projectKey === consts.PROJECT.CIASI_RBC) {
    return RBC_CELL_TYPE_TREE;
  }
  if (consts && projectKey === consts.PROJECT.BONE_MARROW_NTU) {
    return BONE_MARROW_WBC_CELL_TYPE_TREE;
  }
  return WBC_CELL_TYPE_TREE;
}

export function collectNamesFromTree(
  tree: CellTypeMenuOption | CellTypeMenuOption[] | null | undefined,
): string[] {
  const names = new Set<string>();
  const walk = (nodes: CellTypeMenuOption | CellTypeMenuOption[] | null | undefined): void => {
    if (!nodes) return;
    if (Array.isArray(nodes)) {
      nodes.forEach((node) => walk(node));
      return;
    }
    if (nodes.name) names.add(nodes.name);
    if (nodes.children && nodes.children.length) {
      walk(nodes.children);
    }
  };
  walk(tree);
  return Array.from(names);
}

export const WBC_TREE_NAMES = collectNamesFromTree(WBC_CELL_TYPE_TREE);
export const BONE_MARROW_WBC_TREE_NAMES = collectNamesFromTree(BONE_MARROW_WBC_CELL_TYPE_TREE);
export const XAI_NTU_WBC_TREE_NAMES = collectNamesFromTree(XAI_NTU_WBC_CELL_TYPE_TREE);
export const LPC_RBC_ASUS_WBC_TREE_NAMES = collectNamesFromTree(LPC_RBC_ASUS_WBC_CELL_TYPE_TREE);
export const CIASI_RBC_TREE_NAMES = collectNamesFromTree(RBC_CELL_TYPE_TREE);
export const WBC_CELL_TYPES = Array.from(
  new Set([
    ...WBC_TREE_NAMES,
    ...BONE_MARROW_WBC_TREE_NAMES,
    ...XAI_NTU_WBC_TREE_NAMES,
    ...LPC_RBC_ASUS_WBC_TREE_NAMES,
    ...CIASI_RBC_TREE_NAMES,
  ]),
);
export const MERGED_WBC_TREE_NAMES = [...WBC_CELL_TYPES];

export function getAllCellTypes(isRbc: boolean) {
  if (isRbc) {
    return RBC_CELL_TYPES;
  }
  return MERGED_WBC_TREE_NAMES; // Caution: the order is inconsistent with the menu tree
}

export default {
  getAllCellTypes,
  getDrawOptions,
};
